import { createContext, useCallback, useContext, useEffect, useState } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND}/api/auth/me`, { credentials: "include" });
      if (!response.ok) throw new Error("Not authenticated");
      const data = await response.json();
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If we're returning from Google OAuth, AuthCallback will handle the exchange;
    // skip the /me probe so we don't get a spurious 401 before the cookie is set.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const exchangeSession = useCallback(async (sessionId) => {
    const response = await fetch(`${BACKEND}/api/auth/session`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(
        typeof data.detail === "string" ? data.detail : "We could not verify your Google sign-in."
      );
    }
    const data = await response.json();
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${BACKEND}/api/auth/logout`, { method: "POST", credentials: "include" });
    } catch {
      /* ignore */
    }
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, checkAuth, exchangeSession, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
